import {
  AUDIENCE_FIELD_KEYS,
  BRAND_FIELD_DEFINITIONS,
  BRAND_FIELD_KEYS,
  BRAND_LIMITS,
  BRAND_WIZARD_STEPS,
  VALUE_PHILOSOPHIES,
  canonicalSources,
  type AudienceFieldKey,
  type BrandAudienceInput,
  type BrandFieldInput,
  type BrandFieldKey,
  type BrandFieldsInput,
  type BrandImportChangesView,
  type BrandRereadValue,
  type BrandSourceTag,
  type BrandTextChange,
  type BrandWizardStep,
} from '@mg/shared';
import {
  brandDraftReducer,
  isEmptyValue,
  sameValue,
  stepFieldKeys,
  type BrandDraft,
  type DraftAction,
  type DraftAudience,
  type DraftField,
  type FieldValue,
} from '../draft/brandDraft';

/**
 * Revisit Brand Setup's "Re-read my sources" in the draft (spec:127-139, :349; web.md §5.8), pure:
 * - which of a re-read's proposed values go into the wizard's draft, and as which draft actions;
 * - what Show changes draws for a field: a text field's word changes (the server's, E37), and a list's
 *   added and removed items (worked out here: tag lists and the guidelines, E37).
 *
 * The rule a proposal follows is the import's (spec:346-349): it never overwrites what the person has. A
 * proposed value goes in only where the field still holds exactly the saved value the re-read started from
 * (its baseline): not where the person changed it, and not where a save since has. It never clears a field,
 * and nothing is saved until the person saves the step.
 */

// ── Lists: added and removed items, in reading order ───────────────────────────

export type ListChangeKind = 'same' | 'added' | 'removed';

export interface ListChange {
  kind: ListChangeKind;
  item: string;
}

/**
 * A list's changes from `saved` to `current`, item by item (exact text; a stored list may hold the same
 * item twice, E4): the longest run of items both keep, in order, with what was removed and what was added
 * at the place each happened, a removal before an addition at the same place (a reworded item reads as the
 * old one struck through, then the new one).
 */
export function listDiff(saved: readonly string[], current: readonly string[]): ListChange[] {
  const n = saved.length;
  const m = current.length;
  // kept[i][j]: how many items saved[i..] and current[j..] keep in common, in order.
  const kept: number[][] = Array.from({ length: n + 1 }, () => new Array<number>(m + 1).fill(0));
  for (let i = n - 1; i >= 0; i -= 1) {
    for (let j = m - 1; j >= 0; j -= 1) {
      kept[i][j] = saved[i] === current[j] ? kept[i + 1][j + 1] + 1 : Math.max(kept[i + 1][j], kept[i][j + 1]);
    }
  }
  const out: ListChange[] = [];
  let i = 0;
  let j = 0;
  while (i < n && j < m) {
    if (saved[i] === current[j]) {
      out.push({ kind: 'same', item: current[j] });
      i += 1;
      j += 1;
    } else if (kept[i + 1][j] >= kept[i][j + 1]) {
      out.push({ kind: 'removed', item: saved[i] });
      i += 1;
    } else {
      out.push({ kind: 'added', item: current[j] });
      j += 1;
    }
  }
  for (; i < n; i += 1) out.push({ kind: 'removed', item: saved[i] });
  for (; j < m; j += 1) out.push({ kind: 'added', item: current[j] });
  return out;
}

// ── Text: the server's word changes, while they still describe the text ───────

/** Whether the 'same' and 'added' parts, in order, spell `text`: the one state the changes describe. */
export function spellsText(changes: readonly BrandTextChange[], text: string): boolean {
  return (
    changes
      .filter((change) => change.kind !== 'removed')
      .map((change) => change.text)
      .join('') === text
  );
}

const textOf = (value: FieldValue | undefined): string => (typeof value === 'string' ? value : '');

const CHANGE_KINDS: readonly string[] = ['same', 'added', 'removed'];

/** Word changes as the server sends them (checked: a part the renderer cannot read is never drawn). */
function isTextChanges(changes: unknown): changes is BrandTextChange[] {
  return (
    Array.isArray(changes) &&
    changes.length > 0 &&
    changes.every(
      (c) => typeof c === 'object' && c !== null && CHANGE_KINDS.includes((c as BrandTextChange).kind) && typeof (c as BrandTextChange).text === 'string',
    )
  );
}

/**
 * What a text field's Show changes marks, compared with its saved text (spec:134):
 * - the re-read's own word changes (`proposed.changes`, from the server's word-align, E37) while the field
 *   holds exactly the proposed text and its saved text is still the one the re-read compared against;
 * - a field that was empty and now has text: all of it added; one that had text and now is empty: all of
 *   it removed;
 * - otherwise null: the text is shown as it stands, unmarked (the person changed it since, and only the
 *   server lines words up).
 */
export function textChangesToShow({
  saved,
  value,
  proposed,
  baseline,
}: {
  saved: FieldValue;
  value: FieldValue;
  proposed?: BrandRereadValue<unknown> | null;
  baseline?: FieldValue;
}): BrandTextChange[] | null {
  const text = textOf(value);
  const changes = proposed?.changes;
  if (isTextChanges(changes) && sameValue(saved, baseline ?? null) && spellsText(changes, text)) return changes;
  const wasEmpty = isEmptyValue(saved);
  const isEmpty = isEmptyValue(value);
  if (wasEmpty && !isEmpty) return [{ kind: 'added', text }];
  if (!wasEmpty && isEmpty) return [{ kind: 'removed', text: textOf(saved) }];
  return null;
}

// ── Putting a proposal into the draft ─────────────────────────────────────────

/** The steps that hold fields (every step but the start): what Undo and Regenerate drop unsaved changes from. */
export const FIELD_STEPS: readonly BrandWizardStep[] = BRAND_WIZARD_STEPS.map((s) => s.step).filter((step) => step !== 1);

/** The draft with every step's unsaved changes dropped: the saved values. */
export function savedDraft(draft: BrandDraft): BrandDraft {
  return FIELD_STEPS.reduce((s, step) => brandDraftReducer(s, { type: 'discardStep', step }), draft);
}

export interface ProposalPlan {
  /** The draft actions that put the proposal in, in order (dispatch them all, as they are). */
  actions: DraftAction[];
  /**
   * The saved values with the proposal in (the same actions run on savedDraft): what editedSinceProposal
   * compares with. An unsaved change of the person's own, made before the proposal arrived and left alone
   * by it, is not in here, so it still counts as theirs.
   */
  expected: BrandDraft;
  /** The draft card each proposed audience is shown in: card key → its index in `proposal.audiences`. */
  cards: Map<string, number>;
  /** How many values the proposal changed (fields, and audience fields). */
  changed: number;
}

/** A proposed value of the shape its field takes (the server's JSON is checked, never trusted). */
function fitsField(key: BrandFieldKey, value: unknown): value is FieldValue {
  const kind = BRAND_FIELD_DEFINITIONS[key].kind;
  if (kind === 'tags' || kind === 'guidelines') return Array.isArray(value) && value.every((item) => typeof item === 'string');
  if (value === null) return true;
  if (kind === 'valuePhilosophy') return (VALUE_PHILOSOPHIES as readonly unknown[]).includes(value);
  return typeof value === 'string';
}

const fitsAudienceField = (value: unknown): value is string | null => value === null || typeof value === 'string';

/**
 * Whether a proposed value goes into a field: it says something (a proposal never clears a field), it
 * differs from the baseline, and the field still holds exactly that baseline: saved as it was when the
 * re-read started, and not changed by the person since.
 */
function proposalFits(proposed: FieldValue, baseline: FieldValue, f: DraftField): boolean {
  return (
    !isEmptyValue(proposed) &&
    !sameValue(proposed, baseline) &&
    sameValue(f.saved, baseline) &&
    sameValue(f.value, f.saved)
  );
}

/**
 * A proposed value's tags: the field's own, and the sources the re-read took it from (the person's wording
 * is kept, spec:133). A value with no source named reads as from the document, as an import's does. These
 * tags are what keeps a web page's text away from a rule field (O13: suggestionRequest), so a proposal never
 * takes on Manual Entry of its own.
 */
function proposalSources(own: readonly BrandSourceTag[], sources: readonly BrandSourceTag[] | undefined): BrandSourceTag[] {
  const from = canonicalSources(sources ?? []).filter((tag) => tag !== 'manualEntry');
  return canonicalSources([...own, ...(from.length > 0 ? from : ['brandDocument' as const])]);
}

const copyValue = (value: FieldValue): FieldValue => (Array.isArray(value) ? [...value] : value);

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

/**
 * The parts of a changes view that Show changes reads without looking again: the fields and audiences of
 * both the baseline and the proposal, and each proposed audience's fields. Anything else is checked value
 * by value where it is read.
 */
function isChangesView(view: unknown): view is BrandImportChangesView {
  if (!isRecord(view) || !isRecord(view.baseline) || !isRecord(view.proposal)) return false;
  const { baseline, proposal } = view;
  return (
    isRecord(baseline.fields) &&
    Array.isArray(baseline.audiences) &&
    baseline.audiences.every(isRecord) &&
    isRecord(proposal.fields) &&
    Array.isArray(proposal.audiences) &&
    proposal.audiences.every((a) => isRecord(a) && isRecord(a.fields))
  );
}

/** An audience the re-read found that the workspace does not have yet (no id) and that says something. */
const isNewAudience = (proposed: BrandImportChangesView['proposal']['audiences'][number]) =>
  proposed.id === null &&
  AUDIENCE_FIELD_KEYS.some((key) => {
    const value = proposed.fields[key]?.value;
    return fitsAudienceField(value) && !isEmptyValue(value);
  });

/**
 * The draft actions that put a re-read's proposal into the wizard (web.md §5.8: through the reducer's own
 * actions, keeping the saved values as the baseline):
 * - each brand field the proposal changes, through `setFields` UNTOUCHED: like an import's fill it is the
 *   AI's offer, not the person's change, so moving between steps never asks about it; Next saves it with
 *   the step (it has content), Undo and the step's own discard put the saved value back;
 * - each changed field of a saved audience, through `editAudience` with its sources, and each audience only
 *   the sources have, through `addAudience` (a new card goes first, spec:267), up to 10 cards in all. The
 *   reducer has no untouched form for audiences, so step 7 counts these as changes and asks before they are
 *   dropped.
 * The actions are worked out on a copy of the draft with the reducer itself. They touch only fields that
 * hold their saved value, so they run alike on the saved values (`expected`).
 */
export function planProposal(draft: BrandDraft, view: BrandImportChangesView): ProposalPlan {
  if (!isChangesView(view)) throw new Error('The proposed changes are not in the shape Show changes reads.');
  let sim = draft;
  const actions: DraftAction[] = [];
  const push = (action: DraftAction) => {
    actions.push(action);
    sim = brandDraftReducer(sim, action);
  };
  const cards = new Map<string, number>();
  let changed = 0;

  // Brand fields: one setFields with every value that goes in.
  const fieldChanges: Extract<DraftAction, { type: 'setFields' }>['changes'] = [];
  for (const key of BRAND_FIELD_KEYS) {
    const proposed = view.proposal.fields[key] as BrandRereadValue<unknown> | undefined;
    if (!proposed || !fitsField(key, proposed.value)) continue;
    const baseline = view.baseline.fields[key];
    const f = draft.fields[key];
    if (!fitsField(key, baseline ?? null) || !proposalFits(proposed.value, (baseline ?? null) as FieldValue, f)) continue;
    fieldChanges.push({
      key,
      value: copyValue(proposed.value),
      sources: proposalSources(f.sources, proposed.sources),
      touched: false,
    });
  }
  if (fieldChanges.length > 0) {
    push({ type: 'setFields', changes: fieldChanges });
    changed += fieldChanges.length;
  }

  // Saved audiences: the fields the proposal changes, card by card (matched by id, never by name: a
  // re-read may reword an audience's identity).
  view.proposal.audiences.forEach((proposed, index) => {
    if (proposed.id === null) return;
    const card = sim.audiences.find((c) => c.id === proposed.id);
    const baseline = view.baseline.audiences.find((a) => a.id === proposed.id);
    if (!card || !baseline) return;
    let any = false;
    for (const key of AUDIENCE_FIELD_KEYS) {
      const value = proposed.fields[key]?.value;
      const base = baseline[key] ?? null;
      if (!fitsAudienceField(value) || !fitsAudienceField(base)) continue;
      if (!proposalFits(value, base, card.fields[key])) continue;
      const sources = proposalSources(card.fields[key].sources, proposed.fields[key]?.sources);
      push({ type: 'editAudience', key: card.key, field: key, value: value as string, sources });
      changed += 1;
      any = true;
    }
    if (any) cards.set(card.key, index);
  });

  // Audiences only the sources have: as many as fit, in the proposal's order. Each new card goes first,
  // so they are added last one first.
  const room = Math.max(0, BRAND_LIMITS.audiences - sim.audiences.length);
  const fresh = view.proposal.audiences
    .map((proposed, index) => ({ proposed, index }))
    .filter(({ proposed }) => isNewAudience(proposed))
    .slice(0, room);
  for (const { proposed, index } of [...fresh].reverse()) {
    const key = `new-${sim.nextAudienceKey}`;
    push({ type: 'addAudience' });
    if (!sim.audiences.some((c) => c.key === key)) break;
    for (const field of AUDIENCE_FIELD_KEYS) {
      const value = proposed.fields[field]?.value;
      if (!fitsAudienceField(value) || isEmptyValue(value)) continue;
      push({ type: 'editAudience', key, field, value: value as string, sources: proposalSources([], proposed.fields[field]?.sources) });
      changed += 1;
    }
    cards.set(key, index);
  }

  return { actions, expected: actions.reduce(brandDraftReducer, savedDraft(draft)), cards, changed };
}

// ── Has the person changed anything since the proposal went in? ───────────────

const sameCardValues = (a: readonly DraftAudience[], b: readonly DraftAudience[]): boolean =>
  a.length === b.length &&
  a.every((card, i) => AUDIENCE_FIELD_KEYS.every((key) => sameValue(card.fields[key].value, b[i].fields[key].value)));

/**
 * Whether the draft holds an unsaved change of the person's own (`expected`: the saved values with the
 * proposal in): a field that is neither the proposed value nor the saved one, or an audience list that is
 * neither, made since the proposal went in or before it. A step saved or discarded since is not one. Undo
 * and Regenerate drop every unsaved change, so they ask first when there is one (the composer's rule).
 */
export function editedSinceProposal(now: BrandDraft, expected: BrandDraft): boolean {
  for (const key of BRAND_FIELD_KEYS) {
    const f = now.fields[key];
    if (!sameValue(f.value, expected.fields[key].value) && !sameValue(f.value, f.saved)) return true;
  }
  return !sameCardValues(now.audiences, expected.audiences) && !sameCardValues(now.audiences, now.savedAudiences);
}

// ── Undo: back to the re-read's baseline ──────────────────────────────────────

/** One step's save of what Undo puts back: its fields a save changed, or (step 7) the whole audience list. */
export interface BaselineSave {
  step: BrandWizardStep;
  fields?: BrandFieldsInput;
  audiences?: BrandAudienceInput[];
}

type ChangesBaseline = BrandImportChangesView['baseline'];

/** A field's value in the baseline, or undefined when it cannot be read: that is never taken as empty. */
function baselineValue(baseline: ChangesBaseline, key: BrandFieldKey): FieldValue | undefined {
  const value: unknown = baseline.fields[key];
  return fitsField(key, value) ? value : undefined;
}

const readableAudience = (audience: ChangesBaseline['audiences'][number]) =>
  AUDIENCE_FIELD_KEYS.every((key) => fitsAudienceField(audience[key]));

const holdsBaseline = (cards: readonly DraftAudience[], baseline: ChangesBaseline['audiences']) =>
  cards.length === baseline.length &&
  cards.every((card, i) => AUDIENCE_FIELD_KEYS.every((key) => sameValue(card.fields[key].value, baseline[i][key])));

/** What a value goes back to, with its tags from when it was saved (`from`). */
function restoredInput<V extends FieldValue>(value: V, from: DraftField | undefined): BrandFieldInput<V> {
  const empty = isEmptyValue(value);
  return {
    value,
    sources: empty || !from ? [] : canonicalSources(from.savedSources),
    aiSuggested: !empty && (from?.savedAiSuggested ?? false),
  };
}

/** The fields of a step that a save changed since the re-read began; null when there are none. */
function restoredFields(step: BrandWizardStep, now: BrandDraft, baseline: ChangesBaseline, started: BrandDraft | null): BrandFieldsInput | null {
  const fields: Partial<Record<BrandFieldKey, BrandFieldInput<FieldValue>>> = {};
  for (const key of stepFieldKeys(step)) {
    const value = baselineValue(baseline, key);
    const f = now.fields[key];
    const before = started?.fields[key];
    if (value === undefined || sameValue(f.saved, value) || (before && sameValue(f.saved, before.saved))) continue;
    fields[key] = restoredInput(copyValue(value), before && sameValue(before.saved, value) ? before : f);
  }
  return Object.keys(fields).length > 0 ? (fields as BrandFieldsInput) : null;
}

/**
 * The whole audience list as the baseline holds it, in its order (a step save sends the complete list, and an
 * audience it leaves out is deleted): null when a save has not changed it since the re-read began. A saved
 * audience keeps its id; one a save deleted since comes back as a new one.
 */
function restoredAudiences(now: BrandDraft, baseline: ChangesBaseline, started: BrandDraft | null): BrandAudienceInput[] | null {
  const cards = now.savedAudiences;
  if (!baseline.audiences.every(readableAudience) || holdsBaseline(cards, baseline.audiences)) return null;
  if (started && sameCardValues(cards, started.savedAudiences)) return null;
  return baseline.audiences.map((audience) => {
    const card = cards.find((c) => c.id === audience.id);
    const before = started?.savedAudiences.find((c) => c.id === audience.id);
    const fields: BrandAudienceInput['fields'] = {};
    for (const key of AUDIENCE_FIELD_KEYS) {
      const value = audience[key];
      fields[key] = restoredInput(value, before && sameValue(before.fields[key].saved, value) ? before.fields[key] : card?.fields[key]);
    }
    return card ? { id: audience.id, fields } : { fields };
  });
}

/**
 * What Undo must save for every field to hold the baseline the server sent (BrandImportChangesView.baseline;
 * spec:136, :529). Discarding only reaches unsaved changes, and Next saves a proposed value on each step of
 * the review, so a step saved since the re-read began still holds it. A step save is the only write the wizard
 * has, and Next can never reach an earlier step, so Undo saves those steps back itself. It sends only what a
 * save changed: a value that differs from the baseline but has not changed since the re-read began (`started`,
 * the saved values then) is someone else's save, in another tab, and is not this re-read's to undo.
 */
export function planBaselineSaves(now: BrandDraft, view: BrandImportChangesView, started: BrandDraft | null): BaselineSave[] {
  return FIELD_STEPS.flatMap((step): BaselineSave[] => {
    if (step === 7) {
      const audiences = restoredAudiences(now, view.baseline, started);
      return audiences ? [{ step, audiences }] : [];
    }
    const fields = restoredFields(step, now, view.baseline, started);
    return fields ? [{ step, fields }] : [];
  });
}

/** Whether a saved audience value is one the re-read knows: the baseline's or the proposal's, for any audience. */
function isRereadAudienceValue(view: BrandImportChangesView, key: AudienceFieldKey, value: FieldValue): boolean {
  return (
    isEmptyValue(value) ||
    view.baseline.audiences.some((audience) => sameValue(audience[key], value)) ||
    view.proposal.audiences.some((audience) => sameValue(audience.fields[key]?.value ?? null, value))
  );
}

/**
 * Whether Undo would drop something of the person's own, so it asks first: an unsaved change
 * (editedSinceProposal), or, as Undo also reaches what was saved, a saved value it would replace that is not
 * the proposal's: wording of their own, saved on a step of the review.
 */
export function undoDropsEdits(now: BrandDraft, expected: BrandDraft, view: BrandImportChangesView, started: BrandDraft | null): boolean {
  if (editedSinceProposal(now, expected)) return true;
  return planBaselineSaves(now, view, started).some(({ fields, audiences }) => {
    const replaced = Object.keys(fields ?? {}) as BrandFieldKey[];
    if (replaced.some((key) => !sameValue(now.fields[key].saved, (view.proposal.fields[key]?.value ?? null) as FieldValue))) return true;
    if (!audiences) return false;
    return now.savedAudiences.some((card) =>
      AUDIENCE_FIELD_KEYS.some((key) => !isRereadAudienceValue(view, key, card.fields[key].saved)),
    );
  });
}
